import { useNavigate } from 'react-router-dom';
import { ANALYSIS_START_DATE, DAY_WINNER_MIN_UNIT_SHARE, analysisEndDate } from '@/types/promoAttribution';

export function PromoAttributionMethodologyPage() {
  const navigate = useNavigate();
  const end = analysisEndDate();
  const mixPct = Math.round(DAY_WINNER_MIN_UNIT_SHARE * 100);

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">How attribution works</h1>
        <p className="text-sm text-muted-foreground mt-2">
          This page summarises the logic implemented in BigQuery and the dashboard.
        </p>
      </div>

      <Section title="Scope">
        <p>
          Data lives in project <code className="text-xs bg-muted px-1 rounded">just-landing-272719</code>, dataset{' '}
          <code className="text-xs bg-muted px-1 rounded">za_megsapp_promo_rules_engine</code>. Sales and quantities come from{' '}
          <strong>basket_product_data</strong>; promo-data linkage from{' '}
          <strong>allocated_data_product_attribution</strong> joined to <strong>promo_rule</strong>.
        </p>
        <p>
          The dashboard analysis window is fixed: <strong>{ANALYSIS_START_DATE}</strong> through{' '}
          <strong>{end}</strong> (end = server "today" in UTC date).
        </p>
      </Section>

      <Section title="Why no revenue / rand values">
        <p>
          All comparisons on this dashboard are <strong>unit-based</strong>. Monetary
          totals (R values) have been removed for now because the SPAR{' '}
          <code className="text-xs bg-muted px-1 rounded">price</code> field on{' '}
          <code className="text-xs bg-muted px-1 rounded">basket_product_data</code> is not
          consistently per-unit — case-scanned lines send the already-extended line total,
          weighed/loose lines send the full line amount with{' '}
          <code className="text-xs bg-muted px-1 rounded">quantity = 0</code>, and some
          baskets mix conventions across lines. A naive{' '}
          <code className="text-xs bg-muted px-1 rounded">price × quantity</code> aggregate
          overstates revenue by roughly 3× in current data.
        </p>
        <p>
          Units are unaffected by that ambiguity, so unit comparisons (avg daily units,
          uplift %, day-mix percentages) are reliable. Revenue will return once the
          per-line resolver is in place — see the{' '}
          <code className="text-xs bg-muted px-1 rounded">spar-basket-price-ambiguity</code>{' '}
          investigation for details.
        </p>
      </Section>

      <Section title="Why we hide “Promo Data” (alone) from comparison stats">
        <p>
          The four states still exist in the data, but the per-state stat cards
          on the product detail page and the bars on the overview state-comparison
          chart now skip <strong>“Promo Data”</strong> on its own (the case where
          promo-data attribution fired but no till discount was applied to the
          line).
        </p>
        <p>
          In current SPAR data this state has only a handful of days per product —
          typically one or two — so a “% vs standard” comparison computed from it
          is statistical noise (we were rendering values like “−64% vs standard”
          off a 2-day sample). “Promotion and Promo Data” has far more days and
          remains the meaningful comparator vs Standard Price.
        </p>
        <p>
          The state is still visible in the daily timeline colouring and the
          daily breakdown table, where it labels what a specific day actually
          was rather than presenting it as an aggregate stat.
        </p>
      </Section>

      <Section title="Which promo rules count">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Valid rules:</strong> <code className="text-xs bg-muted px-1 rounded">store_scope = &apos;all&apos;</code>, excluding rule IDs{' '}
            <strong>335</strong> and <strong>855</strong> (basket multipliers that would skew results).
          </li>
          <li>
            A basket line is treated as <strong>promo-data-attributed</strong> when there is an attribution row for that
            line's <code className="text-xs bg-muted px-1 rounded">basket_product_data_id</code> under a valid rule in the same date window.
          </li>
        </ul>
      </Section>

      <Section title="Line-level state (four buckets)">
        <p>Each <strong>line item</strong> is classified from till fields plus attribution:</p>
        <p className="font-medium text-foreground">Price discount on the line</p>
        <p>
          A line has a <strong>monetary discount</strong> if either{' '}
          <code className="text-xs bg-muted px-1 rounded">discount_amount &gt; 0</code> or{' '}
          <code className="text-xs bg-muted px-1 rounded">discount_total &gt; 0</code> (SPAR sometimes sends only{' '}
          <code className="text-xs bg-muted px-1 rounded">discount_total</code> on case or multi-buy lines).
        </p>
        <pre className="rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs leading-relaxed text-foreground">
          {`Let D = line has discount (amount or total).
Let A = line has valid promo-data attribution.

Both: D ∧ A
Promo Data: ¬D ∧ A
Promotion: D ∧ ¬A
Standard Price: ¬D ∧ ¬A`}
        </pre>
        <p>
          <strong>Promotion</strong> here means "till price discount without promo-data on that line," not "marketing ran a campaign."
        </p>
      </Section>

      <Section title="Daily label per product (GTIN)">
        <p>
          For each calendar day and each GTIN, lines are bucketed into the four states. Units sold are summed{' '}
          <strong>per state</strong> for that day. The day's <strong>winning state</strong> is the state with the{' '}
          <strong>highest unit total</strong>. Ties are broken deterministically (combined state beats Promo Data beats Promotion beats Standard).
        </p>
        <pre className="rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs leading-relaxed text-foreground">
          {`units_s(d,g) = Σ quantity on day d, product g in state s
winning_state(d,g) = argmax_s units_s(d,g) (with tie-break)`}
        </pre>
      </Section>

      <Section title="Timeline bar colour (Mixed)">
        <p>
          On the{' '}
          <button onClick={() => navigate('/spar-mobile/promo-attribution/products')} className="text-primary underline underline-offset-2">
            product timeline
          </button>
          , a day is shown as <strong>Mixed</strong> if the top state's share of units is below{' '}
          <strong>{mixPct}%</strong>. Constant:{' '}
          <code className="text-xs bg-muted px-1 rounded">DAY_WINNER_MIN_UNIT_SHARE = {DAY_WINNER_MIN_UNIT_SHARE}</code>.
        </p>
        <pre className="rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs text-foreground">
          {`top_share(d,g) = max_s units_s(d,g) / Σ_s units_s(d,g)
if top_share ≥ ${DAY_WINNER_MIN_UNIT_SHARE} → colour = winning state
else → Mixed (see tooltip for full % mix)`}
        </pre>
      </Section>

      <Section title="Overview metrics (by state)">
        <p>
          Rows in <strong>daily_sales</strong> are one per (day, GTIN) with a winning state. Aggregates group by state:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Avg daily units:</strong> average of <code className="text-xs bg-muted px-1 rounded">units_sold</code> across those rows in that state.</li>
          <li><strong>Distinct products:</strong> <code className="text-xs bg-muted px-1 rounded">COUNT(DISTINCT gtin)</code>.</li>
          <li><strong>Days:</strong> <code className="text-xs bg-muted px-1 rounded">COUNT(DISTINCT sale_date)</code> for that state.</li>
          <li><strong>Product-day rows:</strong> <code className="text-xs bg-muted px-1 rounded">COUNT(*)</code> of (day, GTIN) pairs.</li>
        </ul>
      </Section>

      <Section title="Percent change on overview cards">
        <p>For a state S with average daily units u_S and reference state R with u_R:</p>
        <pre className="rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs text-foreground">
          pct_vs_ref(S, R) = (u_S − u_R) / u_R × 100 ,  u_R ≠ 0
        </pre>
        <p>Same relative change form as product-level uplift, but applied to global averages by state.</p>
      </Section>

      <Section title="Product-level uplift (top table)">
        <p>
          For each GTIN with enough days in both "promo-data winning" and "non-promo-data winning" buckets (≥3 each),
          let P = average daily units on days where winning state is Promo Data or Both, and N = average on Standard or Promotion days.
        </p>
        <pre className="rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs text-foreground">
          uplift_pct = (P − N) / N × 100 ,  N ≠ 0
        </pre>
      </Section>

      <Section title="GTIN and rule metadata">
        <p>
          <code className="text-xs bg-muted px-1 rounded">promo_rule_gtin.product_id</code> is not used as a basket GTIN.
          Whether "promo data" applied is driven by which rules actually attributed to each{' '}
          <code className="text-xs bg-muted px-1 rounded">basket_product_data</code> row.
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-card">
      <div className="px-5 py-3 border-b">
        <p className="text-base font-semibold">{title}</p>
      </div>
      <div className="px-5 py-4 text-sm text-muted-foreground space-y-3">{children}</div>
    </div>
  );
}
